import { pb } from '@q6cyber/proto2es';
import { ThreatEntity } from '@q6cyber/proto2es/generated/threatentity';
import { cloneDeep } from 'lodash';

import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

import {
  isIntegerValidator,
  isURLValidator,
  multipleInputValidator,
  requiredFieldValidator,
} from '../validations';
import { commentsForm } from './comments.interface';
import { physicalAddressForm } from './physical-address.interface';

export const DEFAULT_THREAT_ENTITY = new pb.threatentity.ThreatEntity().toJson({
  emitDefaultValues: true,
}) as unknown as pb.threatentity.ThreatEntity;

export const DEFAULT_THREAT_ENTITY_RELATIONSHIP =
  new pb.threatentity.Relationship().toJson({
    emitDefaultValues: true,
  }) as unknown as pb.threatentity.Relationship;

export const DEFAULT_THREAT_ENTITY_REFERENCE =
  new pb.threatentity.AnnotatedReference().toJson({
    emitDefaultValues: true,
  }) as unknown as pb.threatentity.AnnotatedReference;

export const DEFAULT_THREAT_ENTITY_PERMISSION =
  new pb.threatentity.Permission().toJson({
    emitDefaultValues: true,
  }) as unknown as pb.threatentity.Permission;

export const DEFAULT_THREAT_ENTITY_EVIDENCE =
  new pb.threatentity.Evidence().toJson({
    emitDefaultValues: true,
  }) as unknown as pb.threatentity.Evidence;

export const DEFAULT_THREAT_ENTITY_INDICATORS =
  new pb.threatentity.AnnotatedIndicator().toJson({
    emitDefaultValues: true,
  }) as unknown as pb.threatentity.AnnotatedIndicator;

export const DEFAULT_THREAT_ENTITY_ATTRIBUTION =
  new pb.threatentity.Attribution().toJson({
    emitDefaultValues: true,
  }) as unknown as pb.threatentity.Attribution;

export const DEFAULT_THREAT_ENTITY_SOURCE = new pb.threatentity.Access().toJson(
  {
    emitDefaultValues: true,
  }
) as unknown as pb.threatentity.Access;

export const DEFAULT_THREAT_ENTITY_CONTACTS =
  new pb.threatentity.ContactList().toJson({
    emitDefaultValues: true,
  }) as unknown as pb.threatentity.ContactList;

export const DEFAULT_THREAT_ENTITY_COMMUNICATION_INFO =
  new pb.source.Source_CommunicationInfo().toJson({
    emitDefaultValues: true,
  }) as unknown as pb.source.Source_CommunicationInfo;

export const DEFAULT_THREAT_ENTITY_USER_DETAILS =
  new pb.common.UserDetails().toJson({
    emitDefaultValues: true,
  }) as unknown as pb.common.UserDetails;

export const DEFAULT_THREAT_ENTITY_HANDLES =
  new pb.threatentity.AnnotatedCommInfo().toJson({
    emitDefaultValues: true,
  }) as unknown as pb.threatentity.AnnotatedCommInfo;

export const threatEntityForm = (): FormGroup => {
  return new FormGroup({
    id: new FormControl(''),
    name: new FormControl('', [
      requiredFieldValidator,
      Validators.required,
      Validators.maxLength(75),
    ]),
    description: new FormControl(''),
    type: new FormControl('', [requiredFieldValidator, Validators.required]),
    tags: new FormControl([]),
    activities: new FormControl([]),
    references: new FormArray([]),
    relationships: new FormArray([]),
    attribution: new FormArray([]),
    indicators: new FormArray([]),
    contactLists: new FormArray([]),
    accesses: new FormArray([]),
    permissions: new FormArray([]),
    handles: new FormArray([]),
  });
};

export const threatEntityReferenceForm = (
  data = cloneDeep(DEFAULT_THREAT_ENTITY_REFERENCE)
) => {
  const form = new FormGroup<{ [key: string]: AbstractControl }>({
    url: new FormControl('', [
      isURLValidator,
      Validators.required,
      requiredFieldValidator,
    ]),
  });

  form.patchValue(data);

  if (data.comment) {
    const comment = commentsForm(data.comment) as AbstractControl;
    form.addControl('comment', comment);
  }

  return form;
};

export const threatEntityRelationshipsForm = (
  data = cloneDeep(DEFAULT_THREAT_ENTITY_RELATIONSHIP)
) => {
  const form = new FormGroup<{ [key: string]: AbstractControl }>({
    confidence: new FormControl(0, [isIntegerValidator]),
    endDate: new FormControl(null as null | string),
    startDate: new FormControl(null as null | string),
    label: new FormControl(''),
    notes: new FormArray([]),
    permissions: new FormArray([]),
    threatEntityId: new FormControl('', [
      requiredFieldValidator,
      Validators.required,
    ]),
    evidence: new FormArray([]),
  });

  form.patchValue({
    ...(data as any),
  });

  const dataType: any = data;
  if (dataType.type) {
    form.addControl('type', new FormControl(dataType.type));
  } else {
    form.addControl(
      'otherRelationship',
      new FormControl(dataType.otherRelationship)
    );
  }

  const notes = form.get('notes') as FormArray;
  data.notes.forEach(note => {
    notes.push(commentsForm(note));
  });

  const permissions = form.get('permissions') as FormArray;
  data.permissions.forEach(permission => {
    permissions.push(threatEntityPermissionsForm(permission));
  });

  const evidence = form.get('evidence') as FormArray;
  data.evidence.forEach(data => {
    evidence.push(threatEntityEvidenceForm(data));
  });

  return form;
};

export const threatEntityHandlesForm = (
  data = cloneDeep(DEFAULT_THREAT_ENTITY_HANDLES)
) => {
  const form = new FormGroup<{ [key: string]: AbstractControl }>({
    handle: CommunicationInfoForm(true),
    comments: new FormArray([]),
    evidence: new FormArray([]),
  });

  form.patchValue({
    ...(data as any),
  });

  const handleControl = form.get('handle') as FormControl;
  if (data.handle) {
    handleControl.patchValue(data.handle);
  }

  const comments = form.get('comments') as FormArray;
  data.comments.forEach(comment => {
    comments.push(commentsForm(comment));
  });

  const evidence = form.get('evidence') as FormArray;
  data.evidence.forEach(data => {
    evidence.push(threatEntityEvidenceForm(data));
  });

  return form;
};

export const threatEntityEvidenceForm = (
  data = cloneDeep(DEFAULT_THREAT_ENTITY_EVIDENCE)
) => {
  const form = new FormGroup({
    confidence: new FormControl(0, isIntegerValidator),
    notes: new FormArray([]),
    time: new FormControl(null as null | string),
    data: new FormGroup({
      objectId: new FormControl('', [
        requiredFieldValidator,
        Validators.required,
      ]),
      id: new FormControl(''),
      descriptorName: new FormControl(''),
    }),
  });

  form.patchValue({
    ...(data as any),
  });

  const notes = form.get('notes') as FormArray;
  data.notes.forEach(note => {
    notes.push(commentsForm(note));
  });

  //Disable date
  form.get('time')?.disable();

  return form;
};

export const threatEntityPermissionsForm = (
  data = cloneDeep(DEFAULT_THREAT_ENTITY_PERMISSION)
) => {
  const form = new FormGroup({
    id: new FormControl('', [requiredFieldValidator, Validators.required]),
    name: new FormControl(),
    permission: new FormControl('', Validators.required),
  });
  form.patchValue({
    ...(data as any),
    permission: pb.threatentity.Entitlement[data.permission]
      ? data.permission
      : null,
  });

  return form;
};

export const threatEntityIndicatorForm = (
  data = cloneDeep(DEFAULT_THREAT_ENTITY_INDICATORS)
) => {
  const form = new FormGroup({
    selector: new FormGroup({
      indicator: new FormControl('', [
        requiredFieldValidator,
        Validators.required,
      ]),
      infrastructureType: new FormControl(),
      type: new FormControl(),
      description: new FormControl(),
      firstSeen: new FormControl(null as null | string),
      lastSeen: new FormControl(null as null | string),
    }),
    comments: new FormArray([]),
    evidence: new FormArray([]),
  });
  form.patchValue(data as any);

  const selector = form.get('selector') as FormGroup;
  selector.patchValue({
    ...data,
  });

  const comments = form.get('comments') as FormArray;
  data.comments.forEach(comment => {
    comments.push(commentsForm(comment));
  });

  const evidence = form.get('evidence') as FormArray;
  data.evidence.forEach(data => {
    evidence.push(threatEntityEvidenceForm(data));
  });

  return form;
};

export const threatEntitySourceForm = (
  data = cloneDeep(DEFAULT_THREAT_ENTITY_SOURCE),
  isRequired: boolean = true
) => {
  const form = new FormGroup({
    id: new FormControl(null),
    sourceId: new FormControl(
      '',
      isRequired ? [requiredFieldValidator, Validators.required] : []
    ),
    label: new FormControl(''),
    notes: new FormArray([]),
    startDate: new FormControl(null as null | string),
    endDate: new FormControl(null as null | string),
    evidence: new FormArray([]),
    permissions: new FormArray([]),
    confidence: new FormControl(0, isIntegerValidator),
  });
  form.patchValue({
    ...(data as any),
  });

  const notes = form.get('notes') as FormArray;
  data.notes.forEach(note => {
    notes.push(commentsForm(note));
  });

  const evidence = form.get('evidence') as FormArray;
  data.evidence.forEach(data => {
    evidence.push(threatEntityEvidenceForm(data));
  });

  return form;
};
export const threatEntityAttributionsForm = (
  data = cloneDeep(DEFAULT_THREAT_ENTITY_ATTRIBUTION)
) => {
  const form = new FormGroup({
    name: new FormGroup(
      {
        first: new FormControl(''),
        last: new FormControl(''),
        middle: new FormControl(''),
        nickname: new FormControl(''),
        prefix: new FormControl(''),
        suffix: new FormControl(''),
        title: new FormControl(''),
      },
      [multipleInputValidator(['first', 'last', 'middle', 'nickname'])]
    ),
    address: physicalAddressForm(),
    phoneNumbers: new FormControl([]),
    evidence: new FormArray([]),
    comments: new FormArray([]),
    media: new FormGroup({
      imageUrls: new FormControl<string[]>([]),
    }),
  });
  form.patchValue(data as any);

  const comments = form.get('comments') as FormArray;
  data.comments.forEach(comment => {
    comments.push(commentsForm(comment));
  });

  const evidence = form.get('evidence') as FormArray;
  data.evidence.forEach(data => {
    evidence.push(threatEntityEvidenceForm(data));
  });

  return form;
};

export const threatEntityContactsForm = (
  data = cloneDeep(DEFAULT_THREAT_ENTITY_CONTACTS)
) => {
  const form = new FormGroup({
    id: new FormControl(''),
    handle: new FormControl('', [requiredFieldValidator, Validators.required]),
    contacts: new FormArray([]),
  });
  form.patchValue(data as any);

  const contacts = form.get('contacts') as FormArray;
  data.contacts.forEach(contact => {
    contacts.push(CommunicationInfoForm(true, contact));
  });

  return form;
};

export const CommunicationInfoForm = (
  requiredData = false,
  data = cloneDeep(DEFAULT_THREAT_ENTITY_COMMUNICATION_INFO)
) => {
  const form = new FormGroup<{ [key: string]: AbstractControl }>({
    contact: new FormControl(
      '',
      requiredData ? [requiredFieldValidator, Validators.required] : []
    ),
    service: new FormControl('', requiredData ? [Validators.required] : []),
    url: new FormControl(null),
  });
  form.patchValue({
    ...(data as any),
    service: pb.source.Source_CommunicationInfo_Type[data.service]
      ? data.service
      : null,
  });

  form.addControl(
    'userDetails',
    userDetailsForm(data.userDetails ?? undefined)
  );

  return form;
};

const userDetailsForm = (
  data = cloneDeep(DEFAULT_THREAT_ENTITY_USER_DETAILS)
) => {
  const form = new FormGroup<{ [key: string]: AbstractControl }>({
    externalId: new FormControl(''),
    username: new FormControl(''),
    ip: new FormControl(null),
    sourceRef: new FormControl(null),
  });
  form.patchValue({
    ...(data as any),
    sourceRef: data?.sourceRef ?? null,
    ip: data?.ip ?? null,
  });

  const parsedData = new pb.common.UserDetails().fromJson(data as any);
  if (parsedData?.additionalDetails?.case) {
    form.addControl(
      parsedData?.additionalDetails.case,
      new FormControl(parsedData?.additionalDetails?.value.toJson() ?? null)
    );
  }

  return form;
};

export interface ThreatEntityNode extends ThreatEntity {
  isChild?: boolean;
  parentId?: string;
  depth?: number;
}
